<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h1 class="h4 mb-1">{{ reportTitle }} ({{ totalRows }})</h1>
          <small class="text-muted">{{ reportSubtitle }}</small>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger mb-3 py-2">
        {{ errorMessage }}
      </div>

      <div class="card module-filter-card mb-3">
        <div class="card-body py-2">
          <form class="row g-2 align-items-end" @submit.prevent="applyFilters">
            <div v-if="statusOptions.length" class="col-md-3">
              <label class="form-label small fw-semibold mb-1">Statut</label>
              <select v-model="filters.status" class="form-select form-select-sm">
                <option value="">Tous</option>
                <option v-for="option in statusOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>

            <div v-if="typeOptions.length" class="col-md-3">
              <label class="form-label small fw-semibold mb-1">Type</label>
              <select v-model="filters.type" class="form-select form-select-sm">
                <option value="">Tous</option>
                <option v-for="option in typeOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>

            <div class="col-md-2">
              <label class="form-label small fw-semibold mb-1">Date du</label>
              <input v-model="filters.date_from" type="date" class="form-control form-control-sm" />
            </div>

            <div class="col-md-2">
              <label class="form-label small fw-semibold mb-1">Date au</label>
              <input v-model="filters.date_to" type="date" class="form-control form-control-sm" />
            </div>

            <div class="col-12 module-filter-actions mt-2">
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                :disabled="isLoading"
                @click="resetFilters"
              >
                <i class="fa fa-rotate-left me-1" aria-hidden="true"></i>
                Réinitialiser
              </button>
              <button type="submit" class="btn btn-primary btn-sm" :disabled="isLoading">
                <i class="fa fa-search me-1"></i>
                Filtrer
              </button>
            </div>
          </form>
        </div>
      </div>

      <div class="row g-3 mb-3">
        <div v-for="card in summaryCards" :key="card.label" class="col-md-3 col-sm-6">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body py-2">
              <div class="small text-muted">{{ card.label }}</div>
              <div class="fs-5 fw-bold">{{ formattedSummary(card) }}</div>
            </div>
          </div>
        </div>
      </div>

      <BootstrapDataTable
        :state-key="tableStateKey"
        :show-print="false"
        :rows="rows"
        :columns="tableColumns"
        :loading="isLoading"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        client-pagination
        :per-page="table.perPage"
        :active-filter-count="activeFilterCount"
        :searchable="isSearchable"
        v-model:search-value="filters.search"
        search-placeholder="Rechercher référence, produit, fournisseur..."
        empty-text="Aucune donnée trouvée."
        @search-change="handleTableSearch"
        @sort-change="handleSortChange"
        @refresh="fetchReport"
      >
        <template #cell-status="{ row }">
          <span class="badge" :class="statusClass(row.status)">
            {{ statusLabel(row.status) }}
          </span>
        </template>

        <template #cell-type="{ row }">
          <span class="badge" :class="typeClass(row.type)">
            {{ typeLabel(row.type) }}
          </span>
        </template>

        <template #cell-impact="{ row }">
          <span class="badge" :class="row.impact === 'entry' ? 'bg-success' : 'bg-danger'">
            {{ row.impact === 'entry' ? 'Entrée' : 'Sortie' }}
          </span>
        </template>

        <template #cell-is_supplier_return="{ row }">
          <span class="badge" :class="row.is_supplier_return ? 'bg-warning text-dark' : 'bg-light text-dark'">
            {{ row.is_supplier_return ? 'Oui' : 'Non' }}
          </span>
        </template>

        <template v-for="field in currencyFields" :key="field" v-slot:[`cell-${field}`]="{ row }">
          {{ $format.currency(Number(row[field] || 0)) }}
        </template>

        <template v-for="field in dateFields" :key="field" v-slot:[`cell-${field}`]="{ row }">
          {{ row[field] ? $format.date(row[field]) : '-' }}
        </template>
      </BootstrapDataTable>
    </template>
  </AdminLayout>
</template>

<script>
import { countActiveFilters } from '@/support/moduleFilters';
import { defaultReportDateRange } from '@/utils/reportDates';

const defaultFilters = () => {
  const defaultDates = defaultReportDateRange();

  return {
    status: '',
    type: '',
    search: '',
    date_from: defaultDates.date_from,
    date_to: defaultDates.date_to,
  };
};

export default {
  name: 'ModuleReportView',
  data() {
    return {
      rows: [],
      isLoading: false,
      errorMessage: '',
      filters: defaultFilters(),
      summary: {
        total: 0,
        sum: {},
      },
      table: {
        sortBy: 'created_at',
        sortDir: 'desc',
        perPage: 10,
      },
      currencyFields: [
        'amount',
        'total_amount',
        'delivery_fee',
        'sale_total_amount',
        'purchase_total_amount',
        'purchase_due_amount',
        'unit_price',
        'debit',
        'credit',
        'balance',
        'total_debit',
        'total_credit',
        'opening_amount',
        'expected_amount',
        'closing_amount',
        'difference',
      ],
      dateFields: [
        'created_at',
        'entry_date',
        'validated_at',
        'return_date',
        'payment_date',
        'occurred_at',
        'sent_at',
        'received_at',
        'opened_at',
        'closed_at',
        'delivered_at',
      ],
    };
  },
  computed: {
    tableStateKey() {
      return `reports.${String(this.$route.name || 'module')}`;
    },
    reportConfig() {
      return this.$route.meta.report || {};
    },
    reportTitle() {
      return this.reportConfig.title || 'Rapport';
    },
    reportSubtitle() {
      return this.reportConfig.subtitle || 'Analyse des données enregistrées sur le site courant.';
    },
    totalRows() {
      return Number(this.summary.total || this.rows.length || 0);
    },
    activeFilterCount() {
      return countActiveFilters(this.filters, defaultFilters(), ['search']);
    },
    endpoint() {
      return this.reportConfig.endpoint || '';
    },
    tableColumns() {
      return this.reportConfig.columns || [];
    },
    statusOptions() {
      return this.reportConfig.statusOptions || [];
    },
    typeOptions() {
      return this.reportConfig.typeOptions || [];
    },
    summaryCards() {
      return this.reportConfig.summaryCards || [
        { label: 'Lignes', key: 'total', format: 'number' },
      ];
    },
    isSearchable() {
      return this.reportConfig.searchable !== false;
    },
  },
  watch: {
    '$route.name'() {
      this.resetFilters(false);
      this.fetchReport();
    },
  },
  mounted() {
    this.fetchReport();
  },
  methods: {
    async fetchReport() {
      if (!this.endpoint) return;

      this.isLoading = true;
      this.errorMessage = '';

      try {
        const params = {};
        Object.entries(this.filters).forEach(([key, value]) => {
          if (value !== '') params[key] = value;
        });

        const res = await this.$api.get(this.endpoint, { params });
        const data = res.data.data || res.data;

        this.rows = data.rows || [];
        this.summary = {
          total: data.total || this.rows.length,
          sum: data.sum || {},
        };
      } catch (e) {
        console.error('Erreur rapport module', e);
        this.errorMessage = 'Impossible de charger ce rapport.';
      } finally {
        this.isLoading = false;
      }
    },

    applyFilters() {
      this.fetchReport();
    },

    handleTableSearch(search) {
      this.filters.search = search;
      this.fetchReport();
    },

    resetFilters(fetch = true) {
      this.filters = defaultFilters();

      if (fetch) this.fetchReport();
    },

    handleSortChange({ sortBy, sortDir }) {
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
    },

    valueAtPath(source, path) {
      return String(path || '').split('.').reduce((value, key) => {
        if (value === null || typeof value !== 'object') return undefined;
        return value[key];
      }, source);
    },

    formattedSummary(card) {
      const value = card.key === 'total'
        ? this.summary.total
        : this.valueAtPath({ sum: this.summary.sum }, card.key);

      if (card.format === 'currency') {
        return this.$format.currency(Number(value || 0));
      }

      return Number(value || 0).toLocaleString('fr-FR');
    },

    statusLabel(status) {
      const labels = {
        draft: 'Brouillon',
        closed: 'Clos',
        validated: 'Validé',
        cancelled: 'Annulé',
        sent: 'Envoyé',
        received: 'Reçu',
        open: 'Ouverte',
        pending: 'En attente',
        assigned: 'Assignée',
        delivered: 'Livrée',
        unpaid: 'Non réglé',
        partial: 'Partiel',
        paid: 'Payé',
        posted: 'Comptabilisé',
        resolved: 'Résolue',
      };

      return labels[status] || status || '-';
    },

    statusClass(status) {
      const classes = {
        draft: 'bg-secondary',
        closed: 'bg-info text-dark',
        validated: 'bg-success',
        cancelled: 'bg-danger',
        sent: 'bg-primary',
        received: 'bg-success',
        open: 'bg-success',
        pending: 'bg-warning text-dark',
        assigned: 'bg-primary',
        delivered: 'bg-success',
        unpaid: 'bg-danger',
        partial: 'bg-warning text-dark',
        paid: 'bg-success',
        posted: 'bg-primary',
        resolved: 'bg-success',
      };

      return classes[status] || 'bg-secondary';
    },

    typeLabel(type) {
      const labels = {
        entry: 'Entrée',
        exit: 'Sortie',
      };

      return labels[type] || type || '-';
    },

    typeClass(type) {
      if (type === 'entry') return 'bg-success';
      return 'bg-danger';
    },

  },
};
</script>
